import { ReactNode } from 'react';
import { Truncate } from './Truncate';

export type RankItem = {
  id: string;
  label: string;
  value: number;
  percent?: number;          // 0..1 for the bar fill width; derived if not given
};

function formatK(n: number) {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1000)}K`;
  return String(Math.round(n));
}

type Props = {
  items: RankItem[];
  total?: number;            // for % display; defaults to sum
  formatValue?: (n: number) => ReactNode;
  // When provided, each row becomes a clickable drill-down (e.g. open the
  // customer/product record, or filter the dashboard to that entity).
  onItemClick?: (item: RankItem, index: number) => void;
  // A semantic colour per row (e.g. status red/green). Without it every bar is
  // the accent, fading by rank.
  colorFor?: (item: RankItem, index: number) => string;
};

export function RankList({ items, total, formatValue = formatK, onItemClick, colorFor }: Props) {
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
  const sum = (total ?? items.reduce((s, i) => s + i.value, 0)) || 1;
  const clickable = !!onItemClick;

  return (
    <ul className="space-y-2">
      {items.map((it, i) => {
        const color = colorFor ? colorFor(it, i) : null;
        const fade = colorFor || items.length < 2 ? 1 : Number((1 - (0.65 * i) / (items.length - 1)).toFixed(2));
        const width = Math.max(2, (Math.abs(it.value) / max) * 100);
        const pct = it.percent !== undefined ? it.percent : (it.value / sum);
        return (
          <li key={it.id}
            onClick={clickable ? () => onItemClick!(it, i) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick!(it, i); } } : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            title={clickable ? `Drill into ${it.label}` : undefined}
            className={`grid grid-cols-[minmax(0,2fr)_2fr_auto_auto] gap-3 items-center text-body ${
              clickable ? 'cursor-pointer rounded-md -mx-1 px-1 py-0.5 hover:bg-border-light/60 focus:outline-none focus:ring-2 focus:ring-primary/30' : ''
            }`}>
            <div className="flex items-center gap-2 min-w-0">
              {color && <span data-part="rank-dot" className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />}
              <Truncate text={it.label} className="text-primary-text" />
            </div>
            <div className="h-2 rounded-full bg-border-light overflow-hidden">
              <div data-part="rank-bar" className="h-full rounded-full" style={{ width: `${width}%`, background: color ?? 'var(--primary)', opacity: fade }} />
            </div>
            <div className="text-[0.75rem] font-semibold font-numeric tabular-nums text-primary-text">{formatValue(it.value)}</div>
            <div className="text-meta text-secondary font-numeric tabular-nums w-10 text-right">
              {(pct * 100).toFixed(0)}%
            </div>
          </li>
        );
      })}
    </ul>
  );
}
